import { todayPT } from "@homebound/activesupport";
import { Accordion, Button, Css, FormLines } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { Card } from "src/components/Card";
import { baseDownloadUrl } from "src/context";
import {
  ReviewBidPackageFragment,
  ReviewBidPackageGroupFragment,
  useReviewAndSendStepQuery,
} from "src/routes/bids/add-bid-package/components/5.ReviewAndSendStep.generated";
import { queryResult } from "src/utils";
import { formatWithYear } from "src/utils/dates";
import { openInSelf } from "src/utils/window";
import { BidPackageFormState } from "../AddBidPackagePage";
import { BidScopeFields } from "./BidScopeFields";

export function ReviewAndSendStep({ formState }: { formState: ObjectState<BidPackageFormState> }) {
  const query = useReviewAndSendStepQuery({ variables: { bidPackageGroupId: formState.bidPackageGroupId.value! } });
  return queryResult(query, ({ bidPackageGroup }) => (
    <ReviewAndSendStepView bidPackageGroup={bidPackageGroup} formState={formState} />
  ));
}

type SelectBiddersStepViewProps = {
  bidPackageGroup: ReviewBidPackageGroupFragment;
  formState: ObjectState<BidPackageFormState>;
};

export function ReviewAndSendStepView({ bidPackageGroup, formState }: SelectBiddersStepViewProps) {
  const firstBidPackage = bidPackageGroup.bidPackages[0]!;
  const market = firstBidPackage.projects[0]?.market;
  const scopeSource = firstBidPackage.scopeSource;

  return (
    <div css={Css.df.fdc.aic.$}>
      <div css={Css.df.fdc.aic.my6.$}>
        <div css={Css.xl2.$}>Review and Save Historical Bid</div>
      </div>

      <Card xss={Css.w50.mb3.$}>
        <Accordion defaultExpanded topBorder={false} title={<div css={Css.lg.$}>Package Overview</div>}>
          <FormLines labelStyle="left" width="full">
            <BidScopeFields formState={formState} markets={[market!]} scopeSources={[scopeSource]} disabled />
          </FormLines>
        </Accordion>
      </Card>

      {bidPackageGroup.bidPackages.length > 0 && !formState.isInternalEstimate.value && (
        <Card xss={Css.w50.mb3.$}>
          {bidPackageGroup.bidPackages.map((bp, i) => (
            <BidPackageSection bidPackage={bp} first={i === 0} key={bp.id} />
          ))}
        </Card>
      )}
    </div>
  );
}

type BidPackageSectionProps = { first: boolean; bidPackage: ReviewBidPackageFragment };

function BidPackageSection({ bidPackage, first }: BidPackageSectionProps) {
  const onDownloadTemplate = () => {
    const params = new URLSearchParams({
      type: bidPackage.isUnitBased ? "unitBasedBidPackage" : "planBasedBidPackage",
      bidPackageRequestId: bidPackage.requests.first?.id ?? "",
    });
    openInSelf(`${baseDownloadUrl()}/xlsx?${params.toString()}`);
  };

  return (
    <Accordion defaultExpanded={first} topBorder={false} title={<div css={Css.lg.$}>{bidPackage.name}</div>}>
      <div css={Css.mdSb.mb1.$}>Bidders</div>
      <div css={Css.df.fdc.gap2.mb3.$}>
        {bidPackage.requests.map((request, i) => (
          <div key={request.id} css={Css.bgGray100.br8.p2.df.$}>
            <div css={Css.w("40%").$}>
              <div css={Css.smSb.mb2.$}>Bidder #{i + 1}</div>
              <div css={Css.mdSb.$}>{request.tradePartner.name}</div>
            </div>
            <div>
              <div css={Css.sm.mbPx(4).$}>{request.primaryContact?.name}</div>
              <div css={Css.xs.mbPx(4).$}>
                {request.primaryContact?.officePhone || request.primaryContact?.mobilePhone}
              </div>
              <div css={Css.xsSb.$}>{request.primaryContact?.email}</div>
            </div>
          </div>
        ))}
      </div>
      {!bidPackage.isInternalEstimate && (
        <>
          <div css={Css.mdSb.mb1.$}>Bid Sheet</div>
          <Button
            variant="text"
            onClick={onDownloadTemplate}
            data-testid="csvTemplateLink"
            label={`HomeboundBidPackage-${formatWithYear(todayPT()).replaceAll("/", "-")}.xlsx`}
          />
        </>
      )}
    </Accordion>
  );
}
